/**
 * Вкладка «Маркетинг» или «Продукт и сервис» раздела «Голос клиента» (#848):
 * фильтр потока, блоки строк и меню «Выгрузить» — таблица вкладки, печатная
 * форма и подборка формулировок клиентов. Все три строятся из тех же строк,
 * что экран (`voiceSections`). Блок показывает по 10 строк с перелистыванием
 * (#2314) — выгрузки по-прежнему берут список целиком.
 *
 * Под фильтром темы (#2229) вкладка сужается целиком: строка фильтра берёт
 * числа из эха ответа вкладки, без эха числа не показываются, выгрузки
 * подписаны темой.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceOfCustomerResponse } from '@aica/api-types/schemas';
import { SegmentedButtons } from '@/components/ui/segmented-buttons';
import { VoiceChangesSection } from './VoiceChangesSection';
import { tabSeriesWindow } from './useVoiceSeries';
import { VoiceExportMenu } from './VoiceExportMenu';
import { VoiceSectionPanel } from './VoiceSectionPanel';
import { VoiceTabShell } from './VoiceTabShell';
import { VOICE_PAGE_ROWS } from './VoiceTopList';
import { useVoiceOfCustomer } from './useVoiceOfCustomer';
import { VoiceLoading } from './VoiceLoading';
import { VoiceThemeFilterBlocks } from './VoiceThemeFilterBlocks';
import { VoiceThemeFilterStrip } from './VoiceThemeFilterStrip';
import { useVoiceThemeFilter, useVoiceThemeFilterTab } from './useVoiceThemeFilter';
import {
  countsLowerBound,
  sectionCallsInWindow,
  truncationNote,
  voiceSections,
} from './voice-view-model';
import {
  VOICE_AUDIENCE_SECTIONS,
  type VoiceAudience,
  type VoiceSearch,
} from './voice-search';

interface VoiceAudienceTabProps {
  audience: Extract<VoiceAudience, 'marketing' | 'product'>;
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onOpenCall: (recordId: string) => void;
  upgradeHref?: string;
}

export function VoiceAudienceTab({
  audience,
  search,
  onSearchChange,
  onOpenCall,
  upgradeHref,
}: VoiceAudienceTabProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { query: voice, stream, request } = useVoiceOfCustomer(search, onSearchChange);
  // The picker's rows without cuts: this tab draws no themes block.
  const theme = useVoiceThemeFilter(search, onSearchChange, undefined);
  // The same competitors in the same order in another period, project, stream,
  // organization, demo rule or theme are still a new list: its pages start
  // from the first (#2377). The request's own key is that whole scope; the
  // blocks are re-mounted by it once the answer of that scope is on screen —
  // not while the previous one is kept under a new filter.
  const scope = JSON.stringify(request.key);
  const [shownScope, setShownScope] = useState(scope);
  if (!voice.isPlaceholderData && shownScope !== scope) setShownScope(scope);
  const filter = useVoiceThemeFilterTab(search, theme, [voice]);
  // The theme is being lifted after a refusal: the page reloads without it.
  if (filter.refused) return <VoiceLoading />;
  // «What changed» (#2394 §9 п. 7) — a view of the same blocks, in the address.
  const view = search.voiceSection === 'changes' ? 'changes' : 'all';
  const onToggleRow = (rowId: string) =>
    onSearchChange({ row: search.row === rowId ? undefined : rowId });
  // Under the competitors' rows wherever they are listed — «What changed»
  // too: a shift of unmerged spellings is not a change in the market.
  const competitorNotes = (data: VoiceOfCustomerResponse): string[] => [
    ...(data.competitors.mergeUnavailable
      ? [t('voicePage.competitors.mergeUnavailable')]
      : []),
    ...(data.competitors.unmergedShare > 0
      ? [
          t('voicePage.competitors.unmergedShare', {
            share: Math.round(data.competitors.unmergedShare * 100),
            examples: data.competitors.unmergedExamples.join(', '),
          }),
        ]
      : []),
  ];

  return (
    <VoiceTabShell
      query={voice}
      stream={stream}
      onSearchChange={onSearchChange}
      hint={t(`voicePage.audienceHint.${audience}`)}
      upgradeHref={upgradeHref}
      banner={
        <VoiceThemeFilterStrip
          filter={filter}
          truncated={voice.data ? countsLowerBound(voice.data) : false}
          onSearchChange={onSearchChange}
        />
      }
      exportMenu={(data) =>
        data === undefined ? (
          // No answer at all: on its way, or failed — the tab shows its retry.
          <VoiceExportMenu unavailable={voice.isError ? 'failed' : 'pending'} />
        ) : filter.exportUnavailable ? (
          <VoiceExportMenu unavailable={filter.exportUnavailable} />
        ) : (
          <VoiceExportMenu
            audience={audience}
            sections={voiceSections(data, audience)}
            data={data}
            // While a new filter loads, the screen still shows the previous
            // scope's numbers; an export would file them under the new name.
            disabled={voice.isPlaceholderData}
            theme={filter.exportTheme}
          />
        )
      }
    >
      {(data) => {
        const sections = voiceSections(data, audience);
        // Counted from the facts, the rows cover the whole window whatever the
        // scan hit: only the scan's own numbers — competitor sentiment and
        // wordings — are its latest conversations' (#2596).
        const lowerBound = countsLowerBound(data);
        const truncation = truncationNote(data, sections, t);
        return (
          <VoiceThemeFilterBlocks
            filter={filter}
            seriesWindow={tabSeriesWindow(stream.dateRange, [voice])}
          >
            <SegmentedButtons
              ariaLabel={t('voicePage.layers.sectionsLabel')}
              options={VOICE_AUDIENCE_SECTIONS.map((id) => ({
                id,
                label: t(`voicePage.layers.section.${id}`),
              }))}
              value={view}
              onChange={(id) =>
                onSearchChange({ voiceSection: id === 'all' ? undefined : id })
              }
            />
            {view === 'changes' ? (
              <VoiceChangesSection
                blocks={[{ status: 'ready', sections, lowerBound }]}
                expandedRow={search.row}
                onToggleRow={onToggleRow}
                onOpenCall={onOpenCall}
                notes={
                  sections.some((section) => section.id === 'competitors')
                    ? competitorNotes(data)
                    : []
                }
                // Every block is in «All blocks», the tab's first section.
                sourceSection={() => undefined}
              />
            ) : (
              sections.map((section) => (
                <VoiceSectionPanel
                  key={`${section.id}:${shownScope}`}
                  section={section}
                  callsInWindow={sectionCallsInWindow(section, data)}
                  windowLabel={t('voicePage.windowCalls', {
                    count: sectionCallsInWindow(section, data),
                  })}
                  expandedRow={search.row}
                  onToggleRow={onToggleRow}
                  onOpenCall={onOpenCall}
                  pageSize={VOICE_PAGE_ROWS}
                  lowerBound={lowerBound}
                >
                  {section.id === 'competitors' && (
                    <div className="mt-3 space-y-1 text-xs text-fg-muted">
                      {competitorNotes(data).map((note) => (
                        <p key={note}>{note}</p>
                      ))}
                    </div>
                  )}
                </VoiceSectionPanel>
              ))
            )}
            <div className="space-y-1 text-xs text-fg-muted">
              {truncation && <p>{truncation}</p>}
              {filter.comparable && data.prevTruncated && (
                <p>{t('voicePage.prevTruncated')}</p>
              )}
              {filter.comparable && (
                <p>{t('voicePage.prevWindow', { count: data.prevCallsInWindow })}</p>
              )}
              <p>{t('voicePage.denominatorNote')}</p>
              <p>{t('voicePage.paraphraseNote')}</p>
            </div>
          </VoiceThemeFilterBlocks>
        );
      }}
    </VoiceTabShell>
  );
}
